import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Card, Form, Space, Spin, Typography, message } from 'antd';
import PipeInspectionForm from './components/PipeInspectionForm';
import { applyVcHeaderFallback, decodeDetailPayload, deriveHeatNos, mergeVcOverlay } from './components/inspection-form/inspectionPayloadCodec';
import { useNavigate, useParams } from 'react-router-dom';
import dayjs from 'dayjs';
import { upsertHeaderWithItems, fetchSqlData } from '../../../../../services/api';
import type { QaqcPipeInspectionRecord } from '../../../../../types';
import { FileUploadToolHandle } from '../../../../../components/common/FileUploadTool';
import { useQueryClient } from '@tanstack/react-query';
import Button from '../../../../../components/common/Button';

const { Text } = Typography;

const QaqcPipeInspectionEdit: React.FC = () => {
  const navigate = useNavigate();
  const { report_id } = useParams<{ report_id: string }>();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [row, setRow] = useState<QaqcPipeInspectionRecord | null>(null);
  const initialPayloadsRef = useRef<Record<string, any>>({});
  const fileUploadRef = useRef<FileUploadToolHandle>(null);
  const [uploadPath, setUploadPath] = useState<string>('');
  const [weldMetaMap, setWeldMetaMap] = useState<Record<string, {
    pipe_size?: string | null,
    service_class_name?: string | null,
    weldtype_name?: string | null,
    spool_id?: number | null,
    weld_category?: string | null,
    code_no?: string | null,
    fluid?: string | null,
  }>>({});
  const queryClient = useQueryClient();

  // Map weldingdata_id -> weld_no_rev like Create page
  const weldRevMap = useMemo(() => {
    const map: Record<string, number | null> = {};
    const ids = (row as any)?.weldingdata_ids as (string | number)[] | undefined;
    const revs = (row as any)?.weld_no_revs as (number | null)[] | undefined;
    if (Array.isArray(ids) && Array.isArray(revs)) {
      ids.forEach((id, idx) => { map[String(id)] = revs[idx] ?? null; });
    }
    return map;
  }, [row]);

  useEffect(() => {
    const load = async () => {
      if (!report_id) return;
      setLoading(true);
      try {
        const res = await fetchSqlData('qaqc_pipe_inspection_records', 'report_id', report_id);
        const r: QaqcPipeInspectionRecord | undefined = Array.isArray((res as any)?.data)
          ? (res as any).data?.[0]
          : (Array.isArray(res) ? (res as any)[0] : undefined);
        if (!r) { message.error('Inspection not found'); return; }
        setRow(r);
        setUploadPath(r.upload_path || '');
        form.setFieldsValue({
          report_id: r.report_id,
          issued_by: r.issued_by || '',
          report_date: r.report_date ? dayjs(r.report_date) : undefined,
          report_notes: r.report_notes || '',
          rfi_no: (r as any)?.rfi_id ? String((r as any).rfi_id) : undefined,
          work_permit_ref_num: (r as any)?.work_permit_ref_num || undefined,
          work_permit_description: (r as any)?.work_permit_description || undefined,
          attendance: Array.isArray((r as any)?.attendees) ? (r as any).attendees : [],
          attendee_ids: (r as any)?.attendee_ids || undefined,
        });

        // Fetch details and map payloads to form fields for preloading
        try {
          const det = await fetchSqlData('qaqc_pipe_insp_rcrd_details', 'inspection_report_id', r.report_id);
          const detArr: any[] = Array.isArray((det as any)?.data) ? (det as any).data : (Array.isArray(det) ? (det as any) : []);
          // Build weld meta from persisted columns
          const meta: Record<string, any> = {};
          (detArr || []).forEach((d: any) => {
            const k = String(d.weldingdata_id);
            meta[k] = {
              pipe_size: d.pipe_size ?? null,
              service_class_name: d.service_class_name ?? null,
              weldtype_name: d.weldtype_name ?? null,
              spool_id: ((): number | null => {
                const v = d.spool_id; if (v === null || v === undefined || v === '') return null; const n = Number(v); return Number.isFinite(n) ? n : null;
              })(),
              weld_category: d.weld_category ?? null,
              code_no: d.code_no ?? null,
              fluid: d.fluid ?? null,
            };
          });
          // Enrich with fluid from linked RFI form if available
          try {
            const rfiFormId = (r as any)?.rfi_id;
            if (rfiFormId) {
              const rfiRes = await fetchSqlData('qaqc_pipe_rfi_forms', 'form_id', rfiFormId);
              const rfiRow = Array.isArray((rfiRes as any)?.data) ? (rfiRes as any).data?.[0] : (Array.isArray(rfiRes) ? (rfiRes as any)[0] : undefined);
              if (rfiRow) {
                const ids: string[] = Array.isArray(rfiRow.weldingdata_ids) ? rfiRow.weldingdata_ids : [];
                const fluids: (string | null)[] = Array.isArray((rfiRow as any).fluids) ? (rfiRow as any).fluids : [];
                ids.forEach((wid: string, idx: number) => {
                  const k = String(wid);
                  const f = fluids[idx] ?? null;
                  meta[k] = { ...(meta[k] || {}), fluid: f != null ? String(f) : null };
                });
              }
            }
          } catch {}
          setWeldMetaMap(meta);
          const results: any = {};
          (detArr || []).forEach((d: any) => {
            const wid = String(d.weldingdata_id);
            let payload: any = {};
            try { payload = typeof d.payload === 'string' ? JSON.parse(d.payload) : (d.payload || {}); } catch {}
            // Cache original payload per weld to allow upsert when user makes no changes
            try { initialPayloadsRef.current[wid] = payload && Object.keys(payload).length ? payload : undefined; } catch {}
            results[wid] = { ...(results[wid] || {}), ...decodeDetailPayload(payload) };
          });
          // Ensure we have an object for each weld id present to avoid uncontrolled state
          if (Array.isArray(r.weldingdata_ids)) {
            r.weldingdata_ids.forEach((w: any) => { const k = String(w); results[k] = results[k] || {}; });
          }
          // VC fallback: per-weld welder/tarih payload'da eksikse header dizilerinden doldur
          applyVcHeaderFallback(results, r);
          // Reset then defer set to ensure items are mounted before hydration
          try { form.resetFields(['results']); } catch {}
          setTimeout(() => {
            try { form.setFieldsValue({ results }); } catch {}
          }, 0);
          // Expansion is managed by the shared form component
        } catch {}
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [report_id, form]);

  const onFinish = async (values: any) => {
    if (!row) return;
    try {
      // Finalize uploads if present
      let upload_path: string | null | undefined = row.upload_path ?? null;
      if (fileUploadRef.current && fileUploadRef.current.hasFilesToUpload()) {
        const up = await fileUploadRef.current.finalizeUpload(row.report_id);
        if (!up.success) {
          message.error(up.error || 'File upload failed. Changes not saved.');
          return;
        }
        upload_path = up.folderPath || null;
      }
      // Use single-char codes computed by shared form (fallback to all 'P' if missing)
      const weldingIds: string[] = (row.weldingdata_ids || []).map((w: any) => String(w));
      const resultsArr: string[] = Array.isArray(values?.results_codes) && values.results_codes.length === weldingIds.length
        ? values.results_codes
        : weldingIds.map(() => 'P');

      // Build items batch for transactional save
      const detailsPayloads: any[] = Array.isArray(values?.details_payloads) ? values.details_payloads : [];
      const resultsObj: any = (form.getFieldValue('results') || values?.results || {});
      const getWelderStr = (wid: string, layer: 'flw'|'fw') => {
        const v = form.getFieldValue(['results', wid, 'vc', `${layer}_welder`]);
        const s = (v != null ? String(v) : '').trim();
        return s;
      };
      const items_columns = ['inspection_report_id','weldingdata_id','weld_no_rev','payload','pipe_size','service_class_name','weldtype_name','spool_id','weld_category','code_no','fluid'];
      const items_values: any[][] = weldingIds.map((wid, idx) => {
        const base = (detailsPayloads[idx] !== undefined && detailsPayloads[idx] !== null)
          ? detailsPayloads[idx]
          : (initialPayloadsRef.current[String(wid)] || {});
        // Overlay latest welder strings from form results to avoid any effect timing issues
        const r = resultsObj?.[wid]?.vc || {};
        const baseVc = (base && typeof base === 'object' && (base as any).vc && typeof (base as any).vc === 'object') ? (base as any).vc : {};
        const flw = (() => {
          const s = getWelderStr(wid, 'flw') || (r?.flw_welder != null ? String(r.flw_welder) : '').trim();
          if (s) return s;
          const b = (baseVc?.flw_welder != null ? String(baseVc.flw_welder) : '').trim();
          return b || '';
        })();
        const fw = (() => {
          const s = getWelderStr(wid, 'fw') || (r?.fw_welder != null ? String(r.fw_welder) : '').trim();
          if (s) return s;
          const b = (baseVc?.fw_welder != null ? String(baseVc.fw_welder) : '').trim();
          return b || '';
        })();
        const flwDate = (() => {
          const v = form.getFieldValue(['results', wid, 'vc', 'flw_date']);
          const s = v?.format ? v.format('YYYY-MM-DD') : (v ? String(v) : '').trim();
          if (s) return s;
          const rS = (r?.flw_date != null ? String(r.flw_date) : '').trim();
          if (rS) return rS;
          const b = (baseVc?.flw_date != null ? String(baseVc.flw_date) : '').trim();
          return b || '';
        })();
        const fwDate = (() => {
          const v = form.getFieldValue(['results', wid, 'vc', 'fw_date']);
          const s = v?.format ? v.format('YYYY-MM-DD') : (v ? String(v) : '').trim();
          if (s) return s;
          const rS = (r?.fw_date != null ? String(r.fw_date) : '').trim();
          if (rS) return rS;
          const b = (baseVc?.fw_date != null ? String(baseVc.fw_date) : '').trim();
          return b || '';
        })();
        const merged = mergeVcOverlay(base, { flw, fw, flwDate, fwDate });
        const payloadStr = (() => { try { return JSON.stringify(merged || {}); } catch { return '{}'; } })();
        const meta = (weldMetaMap as any)?.[wid] || {};
        const weld_no_rev = weldRevMap?.[wid] ?? null;
        const spoolId = (() => {
          const v = meta.spool_id;
          if (v === null || v === undefined || v === '') return null;
          const n = Number(v);
          return Number.isFinite(n) ? n : null;
        })();
        return [
          row.report_id,
          wid,
          weld_no_rev,
          payloadStr,
          meta.pipe_size ?? null,
          meta.service_class_name ?? null,
          meta.weldtype_name ?? null,
          spoolId,
          meta.weld_category ?? null,
          meta.code_no ?? null,
          meta.fluid ?? null,
        ];
      });

      // Derive header-level heat lists (aligned with weldingIds order)
      // Base secimi Edit'e ozgu: form payload'i yoksa ilk yuklemedeki payload (initialPayloadsRef)
      const { heat_no1s, heat_no2s } = deriveHeatNos(weldingIds.map((wid, idx) =>
        (detailsPayloads[idx] !== undefined && detailsPayloads[idx] !== null)
          ? detailsPayloads[idx]
          : (initialPayloadsRef.current[String(wid)] || {})
      ));

      const flw_welders: (string | null)[] = weldingIds.map((wid, idx) => {
        const s1 = getWelderStr(wid, 'flw') || (resultsObj?.[wid]?.vc?.flw_welder != null ? String(resultsObj[wid].vc.flw_welder) : '').trim();
        if (s1) return s1;
        const base = (detailsPayloads[idx] !== undefined && detailsPayloads[idx] !== null)
          ? detailsPayloads[idx]
          : (initialPayloadsRef.current[String(wid)] || {});
        const vc = (base && typeof base === 'object') ? ((base as any).vc || {}) : {};
        const s2 = (vc?.flw_welder != null ? String(vc.flw_welder) : '').trim();
        return s2 ? s2 : null;
      });
      const fw_welders: (string | null)[] = weldingIds.map((wid, idx) => {
        const s1 = getWelderStr(wid, 'fw') || (resultsObj?.[wid]?.vc?.fw_welder != null ? String(resultsObj[wid].vc.fw_welder) : '').trim();
        if (s1) return s1;
        const base = (detailsPayloads[idx] !== undefined && detailsPayloads[idx] !== null)
          ? detailsPayloads[idx]
          : (initialPayloadsRef.current[String(wid)] || {});
        const vc = (base && typeof base === 'object') ? ((base as any).vc || {}) : {};
        const s2 = (vc?.fw_welder != null ? String(vc.fw_welder) : '').trim();
        return s2 ? s2 : null;
      });

      const flw_dates: (string | null)[] = weldingIds.map((wid, idx) => {
        const v = form.getFieldValue(['results', wid, 'vc', 'flw_date']);
        const s1 = v?.format ? v.format('YYYY-MM-DD') : (v ? String(v) : '').trim();
        if (s1) return s1;
        const base = (detailsPayloads[idx] !== undefined && detailsPayloads[idx] !== null)
          ? detailsPayloads[idx]
          : (initialPayloadsRef.current[String(wid)] || {});
        const vc = (base && typeof base === 'object') ? ((base as any).vc || {}) : {};
        const s2 = (vc?.flw_date != null ? String(vc.flw_date) : '').trim();
        return s2 ? s2 : null;
      });
      const fw_dates: (string | null)[] = weldingIds.map((wid, idx) => {
        const v = form.getFieldValue(['results', wid, 'vc', 'fw_date']);
        const s1 = v?.format ? v.format('YYYY-MM-DD') : (v ? String(v) : '').trim();
        if (s1) return s1;
        const base = (detailsPayloads[idx] !== undefined && detailsPayloads[idx] !== null)
          ? detailsPayloads[idx]
          : (initialPayloadsRef.current[String(wid)] || {});
        const vc = (base && typeof base === 'object') ? ((base as any).vc || {}) : {};
        const s2 = (vc?.fw_date != null ? String(vc.fw_date) : '').trim();
        return s2 ? s2 : null;
      });

      try {} catch {}

      // Transactional header+items upsert
      try {
        // Flags for header derivation by inspection type
        const isVC = String(row?.inspection_type || '').toUpperCase() === 'VC';
        const len = weldingIds.length;
        const resp = await upsertHeaderWithItems({
          header_table: 'qaqc_pipe_inspection_records',
          header_id: row.id,
          header_data: {
            issued_by: values.issued_by,
            report_date: values.report_date?.format?.('YYYY-MM-DD') || values.report_date,
            report_notes: values.report_notes,
            upload_path,
            attendees: Array.isArray(values.attendance) ? values.attendance : [],
            attendee_ids: Array.isArray(values.attendee_ids) ? values.attendee_ids : undefined,
            results: resultsArr,
            ...(isVC ? {
              flw_welders,
              fw_welders,
              flw_dates,
              fw_dates,
            } : {
              heat_no1s,
              heat_no2s,
            }),
          },
          items_table: 'qaqc_pipe_insp_rcrd_details',
          items_columns,
          items_values,
          items_conflict_keys: ['inspection_report_id','weldingdata_id'],
          items_update_columns: ['payload','pipe_size','service_class_name','weldtype_name','spool_id','weld_category','code_no','fluid'],
        });
        if (!resp?.success) {
          message.error('Failed to update inspection.');
          return;
        }
      } catch (e) {
        message.error('Failed to update inspection.');
        return;
      }
      setUploadPath(upload_path || '');
      try { await queryClient.invalidateQueries({ queryKey: ['qaqc_pipe_inspection_records'] }); } catch {}
      message.success('Inspection updated');
      navigate(-1);
    } catch (e) {
      message.error('Failed to update');
    }
  };

  return (
    <Card
      title={
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <span>
            <a onClick={() => navigate(-1)} style={{ marginRight: 16, fontSize: 16 }}>&larr; Back</a>
            Edit Pipe Inspection{row?.report_id ? ` — ${row.report_id}` : ''}
          </span>
        </div>
      }
      style={{ maxWidth: 800, margin: '0 auto' }}
    >
      <Spin spinning={loading}>
        <Form form={form} layout="vertical" onFinish={onFinish} size="small">
          <PipeInspectionForm
            form={form}
            mode="edit"
            inspectionType={row?.inspection_type}
            weldingdataIds={row?.weldingdata_ids || []}
            uploadPath={uploadPath}
            fileUploadRef={fileUploadRef}
            weldRevMap={weldRevMap}
            weldMetaMap={weldMetaMap}
          />

          <Space style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 12 }}>
            <Button onClick={() => navigate(-1)}>Cancel</Button>
            <Button action="edit" type="primary" htmlType="submit">Save Changes</Button>
          </Space>
        </Form>
      </Spin>
    </Card>
  );
};

export default QaqcPipeInspectionEdit;
